import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, RefreshCw, Trash2, Mail, Phone, Building2, CheckCheck, Inbox, Loader2, Newspaper, Users, Download, BarChart3, Globe, Monitor, Smartphone, Tablet, MousePointerClick, Compass, ShoppingBag, User, Trophy, FileDown, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'arge_admin_token';

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [tab, setTab] = useState('enquiries');
  const [subs, setSubs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [apps, setApps] = useState([]);
  const [articles, setArticles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backfilling, setBackfilling] = useState(false);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setSubs([]);
    setSubscribers([]);
    setOrders([]);
    setApps([]);
    setAnalytics(null);
  }, []);

  const fetchSubs = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/submissions`, authHeaders());
      setSubs(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); setLoginError('Session expired. Please sign in again.'); }
      else setError('Could not load submissions.');
    } finally { setLoading(false); }
  }, [token, authHeaders, logout]);

  const fetchSubscribers = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const [res, wl] = await Promise.all([
        axios.get(`${API}/admin/subscribers`, authHeaders()),
        axios.get(`${API}/admin/dopamine-waitlist`, authHeaders()),
      ]);
      setSubscribers(res.data);
      setWaitlist(wl.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); setLoginError('Session expired. Please sign in again.'); }
      else setError('Could not load subscribers.');
    } finally { setLoading(false); }
  }, [token, authHeaders, logout]);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/analytics`, authHeaders());
      setAnalytics(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); setLoginError('Session expired. Please sign in again.'); }
      else setError('Could not load analytics.');
    } finally { setLoading(false); }
  }, [token, authHeaders, logout]);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/orders`, authHeaders());
      setOrders(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); setLoginError('Session expired. Please sign in again.'); }
      else setError('Could not load orders.');
    } finally { setLoading(false); }
  }, [token, authHeaders, logout]);

  const fetchApps = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/competition`, authHeaders());
      setApps(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); setLoginError('Session expired. Please sign in again.'); }
      else setError('Could not load applications.');
    } finally { setLoading(false); }
  }, [token, authHeaders, logout]);

  const fetchArticles = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/articles`, authHeaders());
      setArticles(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); setLoginError('Session expired. Please sign in again.'); }
      else setError('Could not load articles.');
    } finally { setLoading(false); }
  }, [token, authHeaders, logout]);

  const patchArticle = async (slug, fields) => {
    try {
      await axios.patch(`${API}/admin/articles/${slug}`, fields, authHeaders());
      setArticles((s) => s.map((a) => (a.slug === slug ? { ...a, ...fields } : a)));
      return true;
    } catch (e) { window.alert(e?.response?.data?.detail || 'Update failed'); return false; }
  };

  const removeArticle = async (slug) => {
    if (!window.confirm('Delete this article permanently?')) return;
    try { await axios.delete(`${API}/admin/articles/${slug}`, authHeaders());
      setArticles((s) => s.filter((a) => a.slug !== slug)); } catch (e) {}
  };

  const refresh = useCallback(() => {
    if (tab === 'subscribers') fetchSubscribers();
    else if (tab === 'orders') fetchOrders();
    else if (tab === 'analytics') fetchAnalytics();
    else if (tab === 'competition') fetchApps();
    else if (tab === 'journal') fetchArticles();
    else fetchSubs();
  }, [tab, fetchSubs, fetchSubscribers, fetchOrders, fetchAnalytics, fetchApps, fetchArticles]);

  useEffect(() => { if (token) fetchSubs(); }, [token, fetchSubs]);
  useEffect(() => { if (token && tab === 'subscribers') fetchSubscribers(); }, [token, tab, fetchSubscribers]);
  useEffect(() => { if (token && tab === 'orders') fetchOrders(); }, [token, tab, fetchOrders]);
  useEffect(() => { if (token && tab === 'analytics') fetchAnalytics(); }, [token, tab, fetchAnalytics]);
  useEffect(() => { if (token && tab === 'competition') fetchApps(); }, [token, tab, fetchApps]);
  useEffect(() => { if (token && tab === 'journal') fetchArticles(); }, [token, tab, fetchArticles]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true); setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword('');
    } catch (err) {
      setLoginError(err?.response?.status === 401 ? 'Incorrect password.' : 'Login failed. Try again.');
    } finally { setLoggingIn(false); }
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/articles/backfill`, {}, authHeaders());
      const n = res.data?.to_generate ?? 0;
      window.alert(n > 0
        ? `Generating ${n} article(s) in the background. They'll appear in the Journal over the next few minutes.`
        : 'Journal already has a full set of articles — nothing to backfill.');
    } catch (e) {
      window.alert('Could not start backfill. Please try again.');
    } finally { setBackfilling(false); }
  };

  const markRead = async (id) => {    try { await axios.patch(`${API}/admin/submissions/${id}/read`, {}, authHeaders());
      setSubs((s) => s.map((x) => (x.id === id ? { ...x, read: true } : x))); } catch (e) {}
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    try { await axios.delete(`${API}/admin/submissions/${id}`, authHeaders());
      setSubs((s) => s.filter((x) => x.id !== id)); } catch (e) {}
  };

  const removeSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber permanently?')) return;
    try { await axios.delete(`${API}/admin/subscribers/${id}`, authHeaders());
      setSubscribers((s) => s.filter((x) => x.id !== id)); } catch (e) {}
  };

  const exportCsv = () => {
    const rows = [['Email', 'Subscribed On'], ...subscribers.map((s) => [s.email, fmt(s.created_at)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `arge-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };
  const unread = subs.filter((s) => !s.read).length;

  const orderEmail = (o) => o.customer_email || o.shipping?.email || '';
  const orderPhone = (o) => o.customer_phone || o.shipping?.phone || '';
  const orderName = (o) => o.customer_name || o.shipping?.name || '';
  const money = (o) => `${(o.currency || 'gbp').toUpperCase() === 'GBP' ? '£' : ''}${((o.amount || 0) / 100).toFixed(2)}`;

  const exportOrdersCsv = () => {
    const rows = [['Name', 'Email', 'Phone', 'Amount', 'Status', 'Date'],
      ...orders.map((o) => [orderName(o), orderEmail(o), orderPhone(o), money(o), o.payment_status || o.status, fmt(o.created_at)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `arge-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  const paidOrders = orders.filter((o) => o.payment_status === 'paid');

  const markAppRead = async (id) => {
    try { await axios.patch(`${API}/admin/competition/${id}/read`, {}, authHeaders());
      setApps((s) => s.map((x) => (x.id === id ? { ...x, read: true } : x))); } catch (e) {}
  };
  const removeApp = async (id) => {
    if (!window.confirm('Delete this application permanently?')) return;
    try { await axios.delete(`${API}/admin/competition/${id}`, authHeaders());
      setApps((s) => s.filter((x) => x.id !== id)); } catch (e) {}
  };
  const downloadCv = async (a) => {
    try {
      const res = await axios.get(`${API}/admin/competition/${a.id}/cv`, { ...authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement('a');
      el.href = url; el.download = a.cv_filename || `cv-${a.name}.pdf`;
      el.click(); URL.revokeObjectURL(url);
    } catch (e) { window.alert('Could not download CV.'); }
  };
  const exportAppsCsv = () => {
    const rows = [['Status', 'Name', 'Email', 'Phone', 'Country', 'Instagram', 'IG Followers', 'TikTok', 'TT Followers', 'YouTube', 'YT Subs', 'X', 'X Followers', 'Total Reach', 'Cover Letter', 'CV Filename', 'Date'],
      ...apps.map((a) => [a.status === 'partial' ? 'Incomplete' : 'Complete', a.name, a.email, a.phone, a.country, a.instagram, a.instagram_followers, a.tiktok, a.tiktok_followers, a.youtube, a.youtube_followers, a.twitter, a.twitter_followers, a.total_reach, a.cover_letter, a.cv_filename, fmt(a.created_at)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const el = document.createElement('a');
    el.href = url; el.download = `arge-competition-${new Date().toISOString().slice(0, 10)}.csv`;
    el.click(); URL.revokeObjectURL(url);
  };

  // ---- LOGIN VIEW ----
  if (!token) {
    return (
      <div dir="ltr" className="min-h-screen flex items-center justify-center bg-espresso px-6">
        <form onSubmit={login} className="w-full max-w-sm bg-ivory p-10 border-t-2 border-gold">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-14 h-14 flex items-center justify-center bg-espresso text-gold-light mb-4"><Lock className="w-6 h-6" /></div>
            <span className="font-serif text-3xl font-semibold text-espresso">ARGE</span>
            <span className="text-[0.6rem] tracking-[0.4em] uppercase text-gold mt-1">Admin Panel</span>
          </div>
          <label className="block text-[0.68rem] tracking-[0.2em] uppercase text-mocha mb-2">Password</label>
          <input
            type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full bg-transparent border-b border-espresso/25 py-3 text-espresso focus:outline-none focus:border-gold transition-colors"
            placeholder="Enter admin password"
          />
          {loginError && <p data-testid="admin-login-error" className="text-sm text-destructive mt-3">{loginError}</p>}
          <button type="submit" disabled={loggingIn} data-testid="admin-login-submit" className="w-full mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors disabled:opacity-70">
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Sign In
          </button>
        </form>
      </div>
    );
  }

  // ---- DASHBOARD VIEW ----
  return (
    <div dir="ltr" className="min-h-screen bg-cream">
      <header className="bg-espresso text-ivory">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl font-semibold">ARGE</span>
            <span className="text-[0.6rem] tracking-[0.35em] uppercase text-gold-light">Admin</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button onClick={backfill} disabled={backfilling} className="inline-flex items-center gap-2 px-4 py-2 border border-ivory/20 text-xs tracking-[0.15em] uppercase hover:border-gold hover:text-gold-light transition-colors disabled:opacity-60">
              <Newspaper className={`w-4 h-4 ${backfilling ? 'animate-pulse' : ''}`} /> {backfilling ? 'Generating…' : 'Backfill Journal'}
            </button>
            <button onClick={refresh} className="inline-flex items-center gap-2 px-4 py-2 border border-ivory/20 text-xs tracking-[0.15em] uppercase hover:border-gold hover:text-gold-light transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-espresso text-xs tracking-[0.15em] uppercase hover:bg-gold-light transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-2 mb-8 border-b border-gold-soft overflow-x-auto">
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab('enquiries')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase transition-colors -mb-px border-b-2 whitespace-nowrap shrink-0 ${tab === 'enquiries' ? 'border-gold text-espresso' : 'border-transparent text-mocha hover:text-espresso'}`}
          >
            <Inbox className="w-4 h-4" /> Enquiries {subs.length ? `(${subs.length})` : ''}
          </button>
          <button
            data-testid="admin-tab-subscribers"
            onClick={() => setTab('subscribers')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase transition-colors -mb-px border-b-2 whitespace-nowrap shrink-0 ${tab === 'subscribers' ? 'border-gold text-espresso' : 'border-transparent text-mocha hover:text-espresso'}`}
          >
            <Users className="w-4 h-4" /> Subscribers {subscribers.length ? `(${subscribers.length})` : ''}
          </button>
          <button
            data-testid="admin-tab-orders"
            onClick={() => setTab('orders')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase transition-colors -mb-px border-b-2 whitespace-nowrap shrink-0 ${tab === 'orders' ? 'border-gold text-espresso' : 'border-transparent text-mocha hover:text-espresso'}`}
          >
            <ShoppingBag className="w-4 h-4" /> Orders {orders.length ? `(${orders.length})` : ''}
          </button>
          <button
            data-testid="admin-tab-competition"
            onClick={() => setTab('competition')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase transition-colors -mb-px border-b-2 whitespace-nowrap shrink-0 ${tab === 'competition' ? 'border-gold text-espresso' : 'border-transparent text-mocha hover:text-espresso'}`}
          >
            <Trophy className="w-4 h-4" /> Competition {apps.length ? `(${apps.length})` : ''}
          </button>
          <button
            data-testid="admin-tab-journal"
            onClick={() => setTab('journal')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase transition-colors -mb-px border-b-2 whitespace-nowrap shrink-0 ${tab === 'journal' ? 'border-gold text-espresso' : 'border-transparent text-mocha hover:text-espresso'}`}
          >
            <Newspaper className="w-4 h-4" /> Journal
          </button>
          <button
            data-testid="admin-tab-analytics"
            onClick={() => setTab('analytics')}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase transition-colors -mb-px border-b-2 whitespace-nowrap shrink-0 ${tab === 'analytics' ? 'border-gold text-espresso' : 'border-transparent text-mocha hover:text-espresso'}`}
          >
            <BarChart3 className="w-4 h-4" /> Analytics
          </button>
        </div>

        {error && <p className="text-destructive mb-4">{error}</p>}

        {tab === 'enquiries' ? (
          <>
            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{subs.length}</p><p className="text-[0.65rem] tracking-[0.2em] uppercase text-mocha mt-1">Total Enquiries</p></div>
              <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{unread}</p><p className="text-[0.65rem] tracking-[0.2em] uppercase text-mocha mt-1">Unread</p></div>
            </div>

            {loading && subs.length === 0 ? (
              <div className="flex items-center gap-3 text-mocha py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading enquiries…</div>
            ) : subs.length === 0 ? (
              <div className="flex flex-col items-center text-mocha py-24"><Inbox className="w-12 h-12 mb-4 text-gold" /><p>No enquiries yet.</p></div>
            ) : (
              <div className="space-y-4">
                {subs.map((s) => (
                  <div key={s.id} className={`bg-ivory p-6 border ${s.read ? 'border-gold-soft' : 'border-gold'} shadow-sm`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-serif text-2xl text-espresso">{s.name}</h3>
                          {!s.read && <span className="text-[0.6rem] tracking-[0.15em] uppercase bg-gold text-espresso px-2 py-0.5">New</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-mocha">
                          <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-gold" /><a href={`mailto:${s.email}`} className="hover:text-gold">{s.email}</a></span>
                          {s.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-gold" />{s.phone}</span> : null}
                          {s.company ? <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4 text-gold" />{s.company}</span> : null}
                        </div>
                      </div>
                      <span className="text-xs text-mocha whitespace-nowrap">{fmt(s.created_at)}</span>
                    </div>
                    {s.subject ? <p className="mt-4 text-[0.7rem] tracking-[0.15em] uppercase text-gold">{s.subject}</p> : null}
                    <p className="mt-2 text-espresso/90 leading-relaxed whitespace-pre-wrap">{s.message}</p>
                    <div className="mt-5 flex gap-3">
                      {!s.read && (
                        <button onClick={() => markRead(s.id)} className="inline-flex items-center gap-2 px-4 py-2 border border-espresso/25 text-xs tracking-[0.12em] uppercase text-espresso hover:border-gold hover:text-gold transition-colors">
                          <CheckCheck className="w-4 h-4" /> Mark Read
                        </button>
                      )}
                      <button onClick={() => remove(s.id)} className="inline-flex items-center gap-2 px-4 py-2 border border-destructive/40 text-xs tracking-[0.12em] uppercase text-destructive hover:bg-destructive hover:text-ivory transition-colors">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : tab === 'subscribers' ? (
          <div data-testid="subscribers-panel">
            <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
              <div className="flex gap-4">
                <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{subscribers.length}</p><p className="text-[0.65rem] tracking-[0.2em] uppercase text-mocha mt-1">Newsletter Subscribers</p></div>
                <div className="bg-ivory px-6 py-4 border-l-2 border-[#aebf9f]" data-testid="dopamine-waitlist-count"><p className="font-serif text-4xl text-espresso">{waitlist.length}</p><p className="text-[0.65rem] tracking-[0.2em] uppercase text-mocha mt-1">DOPAMINE Waitlist</p></div>
              </div>
              {subscribers.length > 0 && (
                <button onClick={exportCsv} data-testid="export-subscribers-csv" className="inline-flex items-center gap-2 px-4 py-2.5 bg-espresso text-ivory text-xs tracking-[0.15em] uppercase hover:bg-gold transition-colors">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              )}
            </div>

            {loading && subscribers.length === 0 ? (
              <div className="flex items-center gap-3 text-mocha py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading subscribers…</div>
            ) : subscribers.length === 0 ? (
              <div className="flex flex-col items-center text-mocha py-24"><Users className="w-12 h-12 mb-4 text-gold" /><p>No subscribers yet.</p></div>
            ) : (
              <div className="bg-ivory border border-gold-soft divide-y divide-gold-soft">
                {subscribers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-4 px-6 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="w-4 h-4 text-gold shrink-0" />
                      <a href={`mailto:${s.email}`} className="text-espresso hover:text-gold truncate">{s.email}</a>
                    </div>
                    <div className="flex items-center gap-5 shrink-0">
                      <span className="text-xs text-mocha whitespace-nowrap hidden sm:block">{fmt(s.created_at)}</span>
                      <button onClick={() => removeSubscriber(s.id)} className="text-destructive/70 hover:text-destructive transition-colors" title="Remove subscriber">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {waitlist.length > 0 && (
              <div className="mt-10" data-testid="dopamine-waitlist-panel">
                <h3 className="font-serif text-2xl text-espresso mb-4">DOPAMINE Waitlist</h3>
                <div className="bg-ivory border border-gold-soft divide-y divide-gold-soft">
                  {waitlist.map((w) => (
                    <div key={w.id} className="flex items-center justify-between gap-4 px-6 py-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <Mail className="w-4 h-4 text-[#7b8674] shrink-0" />
                        <a href={`mailto:${w.email}`} className="text-espresso hover:text-gold truncate">{w.email}</a>
                      </div>
                      <span className="text-xs text-mocha whitespace-nowrap hidden sm:block">{fmt(w.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : tab === 'orders' ? (
          <div data-testid="orders-panel">
            <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
              <div className="flex gap-4">
                <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{orders.length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Total Orders</p></div>
                <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{paidOrders.length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Paid</p></div>
              </div>
              {orders.length > 0 && (
                <button onClick={exportOrdersCsv} data-testid="export-orders-csv" className="inline-flex items-center gap-2 px-4 py-2.5 bg-espresso text-ivory text-xs tracking-[0.15em] uppercase hover:bg-gold transition-colors">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              )}
            </div>

            {loading && orders.length === 0 ? (
              <div className="flex items-center gap-3 text-mocha py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading orders…</div>
            ) : orders.length === 0 ? (
              <div className="flex flex-col items-center text-mocha py-24"><ShoppingBag className="w-12 h-12 mb-4 text-gold" /><p>No orders yet.</p><p className="text-sm mt-1">Customer emails & phones will appear here after checkout.</p></div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className={`bg-ivory p-6 border ${o.payment_status === 'paid' ? 'border-gold' : 'border-gold-soft'} shadow-sm`} data-testid="order-card">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-serif text-2xl text-espresso inline-flex items-center gap-2"><User className="w-4 h-4 text-gold" />{orderName(o) || 'Customer'}</h3>
                          <span className={`text-[0.6rem] tracking-[0.15em] uppercase px-2 py-0.5 ${o.payment_status === 'paid' ? 'bg-gold text-espresso' : 'bg-cream text-mocha'}`}>{o.payment_status || o.status}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-mocha">
                          {orderEmail(o) ? <a href={`mailto:${orderEmail(o)}`} className="inline-flex items-center gap-1.5 hover:text-gold"><Mail className="w-4 h-4 text-gold" />{orderEmail(o)}</a> : <span className="inline-flex items-center gap-1.5 text-mocha/50"><Mail className="w-4 h-4" />No email</span>}
                          {orderPhone(o) ? <a href={`tel:${orderPhone(o)}`} className="inline-flex items-center gap-1.5 hover:text-gold"><Phone className="w-4 h-4 text-gold" />{orderPhone(o)}</a> : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-serif text-2xl text-espresso">{money(o)}</p>
                        <p className="text-xs text-mocha mt-1 whitespace-nowrap">{fmt(o.created_at)}</p>
                      </div>
                    </div>
                    {Array.isArray(o.items) && o.items.length > 0 && (
                      <p className="mt-3 text-[0.75rem] text-mocha">{o.items.map((it) => `${it.lookup_key} ×${it.quantity}`).join('  ·  ')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'journal' ? (
          <div data-testid="journal-panel">
            <div className="flex gap-4 mb-8 flex-wrap">
              <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{articles.filter((a) => a.status === 'published').length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Published</p></div>
              <div className="bg-ivory px-6 py-4 border-l-2 border-amber-500"><p className="font-serif text-4xl text-espresso">{articles.filter((a) => a.status !== 'published').length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Unpublished</p></div>
            </div>
            {loading && articles.length === 0 ? (
              <div className="flex items-center gap-3 text-mocha py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading articles…</div>
            ) : (
              <div className="space-y-3">
                {articles.map((a) => (
                  <ArticleCard key={a.slug} a={a} onPatch={patchArticle} onDelete={removeArticle} />
                ))}
              </div>
            )}
          </div>
        ) : tab === 'competition' ? (
          <div data-testid="competition-panel">
            <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
              <div className="flex gap-4 flex-wrap">
                <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{apps.filter((a) => a.status !== 'partial').length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Applications</p></div>
                <div className="bg-ivory px-6 py-4 border-l-2 border-amber-500"><p className="font-serif text-4xl text-espresso">{apps.filter((a) => a.status === 'partial').length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Partial Leads</p></div>
                <div className="bg-ivory px-6 py-4 border-l-2 border-gold"><p className="font-serif text-4xl text-espresso">{apps.filter((a) => !a.read && a.status !== 'partial').length}</p><p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">Unread</p></div>
              </div>
              {apps.length > 0 && (
                <button onClick={exportAppsCsv} data-testid="export-competition-csv" className="inline-flex items-center gap-2 px-4 py-2.5 bg-espresso text-ivory text-xs tracking-[0.15em] uppercase hover:bg-gold transition-colors">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              )}
            </div>

            {loading && apps.length === 0 ? (
              <div className="flex items-center gap-3 text-mocha py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading applications…</div>
            ) : apps.length === 0 ? (
              <div className="flex flex-col items-center text-mocha py-24"><Trophy className="w-12 h-12 mb-4 text-gold" /><p>No competition applications yet.</p></div>
            ) : (
              <div className="space-y-4">
                {apps.map((a) => (
                  <div key={a.id} className={`bg-ivory p-6 border ${a.status === 'partial' ? 'border-amber-400/60 border-dashed' : a.read ? 'border-gold-soft' : 'border-gold'} shadow-sm`} data-testid="competition-app-card">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-serif text-2xl text-espresso">{a.name}</h3>
                          {a.status === 'partial' && <span className="text-[0.6rem] tracking-[0.15em] uppercase bg-amber-500 text-ivory px-2 py-0.5" data-testid="partial-badge">Incomplete — follow up</span>}
                          {!a.read && a.status !== 'partial' && <span className="text-[0.6rem] tracking-[0.15em] uppercase bg-gold text-espresso px-2 py-0.5">New</span>}
                          {a.total_reach ? <span className="text-[0.6rem] tracking-[0.15em] uppercase border border-gold text-gold px-2 py-0.5">Reach: {a.total_reach}</span> : null}
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-mocha">
                          <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-gold" /><a href={`mailto:${a.email}`} className="hover:text-gold">{a.email}</a></span>
                          {a.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-gold" />{a.phone}</span> : null}
                          {a.country ? <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" />{a.country}</span> : null}
                        </div>
                      </div>
                      <span className="text-xs text-mocha whitespace-nowrap">{fmt(a.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm">
                      {a.instagram ? <span className="text-espresso">Instagram: <strong>{a.instagram}</strong> <span className="text-mocha">({a.instagram_followers || '?'})</span></span> : null}
                      {a.tiktok ? <span className="text-espresso">TikTok: <strong>{a.tiktok}</strong> <span className="text-mocha">({a.tiktok_followers || '?'})</span></span> : null}
                      {a.youtube ? <span className="text-espresso">YouTube: <strong>{a.youtube}</strong> <span className="text-mocha">({a.youtube_followers || '?'})</span></span> : null}
                      {a.twitter ? <span className="text-espresso">X: <strong>{a.twitter}</strong> <span className="text-mocha">({a.twitter_followers || '?'})</span></span> : null}
                    </div>
                    {a.cover_letter ? (<>
                    <p className="mt-3 text-[0.7rem] tracking-[0.15em] uppercase text-gold">Brand Vision / Cover Letter</p>
                    <p className="mt-1 text-espresso/90 leading-relaxed whitespace-pre-wrap">{a.cover_letter}</p>
                    </>) : null}
                    <div className="mt-5 flex flex-wrap gap-3">
                      {a.cv_path && (
                        <button onClick={() => downloadCv(a)} data-testid="download-cv-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-ivory text-xs tracking-[0.12em] uppercase hover:bg-gold transition-colors">
                          <FileDown className="w-4 h-4" /> Download CV {a.cv_filename ? `(${a.cv_filename})` : ''}
                        </button>
                      )}
                      {!a.read && (
                        <button onClick={() => markAppRead(a.id)} className="inline-flex items-center gap-2 px-4 py-2 border border-espresso/25 text-xs tracking-[0.12em] uppercase text-espresso hover:border-gold hover:text-gold transition-colors">
                          <CheckCheck className="w-4 h-4" /> Mark Read
                        </button>
                      )}
                      <button onClick={() => removeApp(a.id)} className="inline-flex items-center gap-2 px-4 py-2 border border-destructive/40 text-xs tracking-[0.12em] uppercase text-destructive hover:bg-destructive hover:text-ivory transition-colors">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div data-testid="analytics-panel">
            {loading && !analytics ? (
              <div className="flex items-center gap-3 text-mocha py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading analytics…</div>
            ) : !analytics ? (
              <div className="flex flex-col items-center text-mocha py-24"><BarChart3 className="w-12 h-12 mb-4 text-gold" /><p>No analytics yet.</p></div>
            ) : (
              <AnalyticsView data={analytics} />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div className="bg-ivory px-6 py-5 border-l-2 border-gold" data-testid={testid}>
    <p className="font-serif text-4xl text-espresso">{Number(value).toLocaleString('en-GB')}</p>
    <p className="text-[0.62rem] tracking-[0.2em] uppercase text-mocha mt-1">{label}</p>
  </div>
);

const BreakdownList = ({ title, icon: Icon, rows, total }) => (
  <div className="bg-ivory border border-gold-soft p-6">
    <div className="flex items-center gap-2 text-gold mb-4"><Icon className="w-4 h-4" /><h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-espresso">{title}</h3></div>
    {(!rows || rows.length === 0) ? (
      <p className="text-sm text-mocha">No data yet.</p>
    ) : (
      <div className="space-y-3">
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
          return (
            <div key={r.name}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-espresso truncate pr-3">{r.name}</span>
                <span className="text-mocha whitespace-nowrap">{r.count.toLocaleString('en-GB')} · {pct}%</span>
              </div>
              <div className="h-1.5 bg-cream rounded-full overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const ArticleCard = ({ a, onPatch, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };
  const toLocal = (iso) => { try { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); } catch { return ''; } };
  const [form, setForm] = useState({ title: a.title, excerpt: a.excerpt || '', when: toLocal(a.published_at) });
  const published = a.status === 'published';
  const save = async () => {
    const fields = { title: form.title, excerpt: form.excerpt };
    if (form.when) fields.published_at = new Date(form.when).toISOString();
    if (await onPatch(a.slug, fields)) setEditing(false);
  };
  return (
    <div className={`bg-ivory p-5 border ${published ? 'border-gold-soft' : 'border-amber-400/60 border-dashed'}`} data-testid="journal-article-card">
      <div className="flex flex-wrap items-start gap-4">
        <img src={a.cover_image} alt="" className="w-20 h-14 object-cover shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-xl text-espresso leading-snug">{a.title}</h3>
            <span className={`text-[0.58rem] tracking-[0.15em] uppercase px-2 py-0.5 ${published ? 'bg-gold text-espresso' : 'bg-amber-500 text-ivory'}`}>{published ? 'Published' : 'Unpublished'}</span>
          </div>
          <p className="text-xs text-mocha mt-1">{fmt(a.published_at)} · /blog/{a.slug}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditing((e) => !e)} data-testid="article-edit-btn" className="px-3 py-1.5 border border-espresso/25 text-[0.65rem] tracking-[0.12em] uppercase text-espresso hover:border-gold hover:text-gold transition-colors">{editing ? 'Close' : 'Edit'}</button>
          <button onClick={() => onPatch(a.slug, { status: published ? 'draft' : 'published' })} data-testid="article-toggle-btn" className="px-3 py-1.5 border border-espresso/25 text-[0.65rem] tracking-[0.12em] uppercase text-espresso hover:border-gold hover:text-gold transition-colors">{published ? 'Unpublish' : 'Publish'}</button>
          <button onClick={() => onDelete(a.slug)} data-testid="article-delete-btn" className="px-3 py-1.5 border border-destructive/40 text-[0.65rem] tracking-[0.12em] uppercase text-destructive hover:bg-destructive hover:text-ivory transition-colors">Delete</button>
        </div>
      </div>
      {editing && (
        <div className="mt-5 pt-5 border-t border-gold-soft space-y-4" data-testid="article-edit-form">
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="article-title-input" className="w-full bg-transparent border-b border-espresso/20 py-2 text-espresso focus:outline-none focus:border-gold" />
          <textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} rows={3} data-testid="article-excerpt-input" className="w-full bg-cream/50 border border-espresso/15 p-3 text-sm text-espresso focus:outline-none focus:border-gold resize-none" />
          <div className="flex flex-wrap items-end gap-4">
            <label className="text-xs text-mocha">Publish date & time
              <input type="datetime-local" value={form.when} onChange={(e) => setForm((f) => ({ ...f, when: e.target.value }))} data-testid="article-date-input" className="block mt-1 bg-transparent border-b border-espresso/20 py-1.5 text-espresso focus:outline-none focus:border-gold" />
            </label>
            <button onClick={save} data-testid="article-save-btn" className="px-6 py-2.5 bg-espresso text-ivory text-[0.65rem] tracking-[0.15em] uppercase hover:bg-gold transition-colors">Save Changes</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AnalyticsView = ({ data }) => {
  const maxViews = Math.max(1, ...data.daily.map((d) => d.views));
  const sum = (rows) => rows.reduce((a, b) => a + b.count, 0);
  const fmtDay = (s) => { const d = new Date(s + 'T00:00:00'); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); };
  const comp = data.competition;
  const funnel = comp ? [
    { label: 'Competition Page Views · 30d', value: comp.views },
    { label: 'Unique Visitors · 30d', value: comp.unique },
    { label: 'Started Application · 30d', value: comp.starts },
    { label: 'Partial Leads · Follow Up', value: comp.partials || 0 },
    { label: 'Applications Submitted · All Time', value: comp.applications },
  ] : [];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.value));
  return (
    <div className="space-y-10">
      {comp && (
        <div className="bg-espresso p-6" data-testid="competition-funnel">
          <div className="flex items-center gap-2 text-gold-light mb-5"><Trophy className="w-4 h-4" /><h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-ivory">Competition Funnel</h3></div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {funnel.map((f) => (
              <div key={f.label} className="bg-ivory/5 border border-gold/25 px-5 py-4">
                <p className="font-serif text-4xl text-gold-light">{Number(f.value).toLocaleString('en-GB')}</p>
                <p className="text-[0.6rem] tracking-[0.18em] uppercase text-ivory/70 mt-1">{f.label}</p>
                <div className="h-1 bg-ivory/10 mt-3 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${Math.round((f.value / funnelMax) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
          <p className="text-[0.68rem] text-ivory/50 mt-4">
            {comp.unique > 0 ? `${Math.round((comp.starts / comp.unique) * 100)}% of unique visitors start the form` : 'No competition visitors yet'}{comp.starts > 0 ? ` · ${Math.round((comp.applications / comp.starts) * 100)}% of starters submit` : ''}
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Unique Visitors Today" value={data.today.unique} testid="stat-today-unique" />
        <StatCard label="Pageviews Today" value={data.today.views} testid="stat-today-views" />
        <StatCard label="Unique Visitors · 30d" value={data.last30.unique} testid="stat-30d-unique" />
        <StatCard label="Pageviews · 30d" value={data.last30.views} testid="stat-30d-views" />
      </div>

      <div className="bg-ivory border border-gold-soft p-6" data-testid="analytics-daily-chart">
        <div className="flex items-center gap-2 text-gold mb-6"><BarChart3 className="w-4 h-4" /><h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-espresso">Last 30 Days</h3></div>
        <div className="flex items-end gap-1 h-44">
          {data.daily.map((d) => (
            <div key={d.day} className="group relative flex-1 flex flex-col justify-end items-center h-full">
              <div className="w-full bg-gold/30 hover:bg-gold transition-colors rounded-t" style={{ height: `${(d.views / maxViews) * 100}%`, minHeight: '2px' }} />
              <div className="pointer-events-none absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap bg-espresso text-ivory text-[0.65rem] px-2 py-1 rounded z-10">
                {fmtDay(d.day)}: {d.views} views · {d.unique} unique
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[0.6rem] text-mocha mt-2">
          <span>{fmtDay(data.daily[0].day)}</span>
          <span>{fmtDay(data.daily[data.daily.length - 1].day)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownList title="Top Pages" icon={MousePointerClick} rows={data.top_pages} total={sum(data.top_pages)} />
        <BreakdownList title="Traffic Sources" icon={Compass} rows={data.sources} total={sum(data.sources)} />
        <BreakdownList title="Devices" icon={Monitor} rows={data.devices} total={sum(data.devices)} />
        <BreakdownList title="Countries" icon={Globe} rows={data.countries} total={sum(data.countries)} />
      </div>
      <p className="text-[0.7rem] text-mocha/70 text-center">Unique visitors are counted once per day per device (anonymous). Country is best-effort from network headers / IP geolocation.</p>
    </div>
  );
};

export default Admin;
